import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { AvatarDisplay } from '../../components/presentation-components/display-elements-components/avatar-display'
import { darken } from '@mui/material/styles';
import { IUserDetails } from '../../shared/services/user-details/user-details.model';
import UserDetailsService from '../../shared/services/user-details/user-details.service';
import { UserDetailsContext } from '../../context/user-details/user-details';
import ReportActionService from '../../shared/services/report-action/report-action.service';
import { ReportPeriodContext } from '../../context/report-periods/report-periods';
import { IReportAction } from '../../shared/services/report-action/report-action.model';
import { TableDisplay } from '../../components/presentation-components/miscellaneous-components/table-display';
import { ActionStatusChip } from '../../components/presentation-components/emissons-components/overview/emissions-actions/action-status-chip';
import { useNavigate } from 'react-router-dom';
import { IHeadCell } from '../../shared/interfaces/table-display.model';
import { actionPeriodLabel } from '../../shared/services/report-action/report-action-title';
import moment from 'moment';
import { CalendarToday, CheckCircleOutlined, LockOutlined } from '@mui/icons-material';
import { useIsReportPeriodLocked } from '../../hooks/use-is-report-period-locked';

export const ActionsNav = () => {

  const navigate = useNavigate();
  //Services
  const userDetailsService = new UserDetailsService();
  const userDetailsContext = useContext(UserDetailsContext)
  const reportActionService = new ReportActionService()
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userDetails = userDetailsContext.userDetails
  //Hooks
  const isReportPeriodLocked = useIsReportPeriodLocked()
  //State
  const [expanded, setExpanded] = React.useState<string>('panel1');
  const [teamUsers, setTeamUsers] = useState<IUserDetails[]>()
  // Defaults to the signed-in user's own tasks; the avatar row widens it to anyone.
  const [selectedUserId, setSelectedUserId] = useState<string>(userDetails?.id || '')
  const [actions, setActions] = useState<IReportAction[]>()
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [selected, setSelected] = useState<string[]>([]);
  const [showAllAvatars, setShowAllAvatars] = useState<boolean>()
  //Variables
  const hasOrgBanner = !!(userDetailsContext.userDetails?.isLoggedInAsCustomer || (userDetailsContext.userDetails?.isPartner && userDetailsContext.userDetails?.isLoggedInAsClient));
  //Use Effects
  useEffect(() => {
    if (userDetails) {
      userDetailsService.getAllUserDetails(`?status=active`)
        .then((response) => {
          setTeamUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false && user.id !== userDetails.id)))
        })
        .catch(err => {
          console.log(err)
        })
    }
  }, [userDetails])

  // The pane can mount before the session user has loaded; pre-select them once they have.
  useEffect(() => {
    if (userDetails?.id && selectedUserId === '') {
      setSelectedUserId(userDetails.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails?.id])

  useEffect(() => {
    if (reportPeriodContext.lastSelectedOpenReportId && selectedUserId !== '') {
      getTableContents()
    }
  }, [reportPeriodContext.lastSelectedOpenReportId, inputPage, perPage, change, expanded, selectedUserId])

  const getTableContents = () => {
    if (reportPeriodContext.lastSelectedOpenReportId && expanded === 'panel1') {
      reportActionService.getReportActionsWithPagination(reportPeriodContext.lastSelectedOpenReportId, `&userId=${selectedUserId}&status=outstanding`, inputPage, perPage)
        .then(response => {
          setActions(response.rows)
          setTotalRecords(response.count);
        })
        .catch(err => {
          console.log(err)
        })
    } else if (reportPeriodContext.lastSelectedOpenReportId && expanded === 'panel2') {
      reportActionService.getReportActionsWithPagination(reportPeriodContext.lastSelectedOpenReportId, `&userId=${selectedUserId}&status=upcoming`, inputPage, perPage)
        .then(response => {
          setActions(response.rows)
          setTotalRecords(response.count);
        })
        .catch(err => {
          console.log(err)
        })
    } else if (reportPeriodContext.lastSelectedOpenReportId && expanded === 'panel3') {
      reportActionService.getReportActionsWithPagination(reportPeriodContext.lastSelectedOpenReportId, `&userId=${selectedUserId}&status=completed`, inputPage, perPage)
        .then(response => {
          setActions(response.rows)
          setTotalRecords(response.count);
        })
        .catch(err => {
          console.log(err)
        })
    }
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'title', numeric: false, label: 'Task', sortable: false, isHeading: true },
    { id: 'date', numeric: false, label: 'Date', sortable: false, oneRow: true },
    { id: 'userId', numeric: false, label: 'Owner', sortable: false, oneRow: true },
  ];

  const rows = actions?.map((action: any) => {
    return {
      id: action.id,
      title: action.status
        ? (
          <Stack display='flex' flexDirection='row' gap={1} alignItems='center'>
            <Typography>{action.title}</Typography>
            <ActionStatusChip status={action.status} reason={action.statusReason} />
          </Stack>
        )
        : action.title,
      date: (
        <Stack display='flex' flexDirection='row' gap={1} alignItems='center'>
          <CalendarToday sx={{ color: 'text.secondary' }} fontSize='small' />
          <Typography>{actionPeriodLabel(action)}</Typography>
        </Stack>
      ),
      userId: action.userId ? (
        <Stack display='flex' flexDirection='row' gap={1} alignItems='center'>
          <AvatarDisplay color={action.user.avatarColor} width={'25px'} height={'25px'} padding={0} userDetails={action.user} fontSize={'0.75rem'} />
          <Typography> {action.user.firstName.charAt(0).toUpperCase() + action.user.firstName.slice(1).toLowerCase() + ' ' + action.user.surname.charAt(0).toUpperCase() + action.user.surname.slice(1).toLowerCase()}</Typography>
        </Stack>
      ) :
        (
          <Stack display='flex' flexDirection='row' gap={1} alignItems='center'>
            <AvatarDisplay color={'#e7e7e7'} width={'25px'} height={'25px'} padding={0} fontSize={'0.75rem'} />
            <Typography> Unassigned</Typography>
          </Stack>
        )
    }
  });

  //Table Event Handlers
  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      reportActionService.checkIfActionReportOrReduction(selectedRowId[0])
        .then(response => {
          if (response.type === 'reportAction') {
            // Tasks created from readiness findings carry the finding's deep link instead of
            // an entity entry form.
            if (response.action.actionCode === 'readiness') {
              navigate(response.action.deepLink || '/readiness')
              return
            }
            navigate(`/emissions/overview/group/${response.action.groupId}?actionId=${response.action.id}`, {
              state: {
                response
              }
            })
          }
          // if (response.type === 'reductionAction') {
          //   navigate(`/reduction-plan/actions?actionId=${selectedRowId[0]}`, {
          //     state: {
          //       showDialog: true,
          //     }
          //   })
          // }
        }).catch(err => console.log(err))
    }
  }

  const filterByUser = (userId: string) => {
    setSelectedUserId(userId)
  }

  const handleChange = (e: SelectChangeEvent<string>) => {
    setExpanded(e.target.value);
  };

  const showAllAvatarsHandler = () => {
    setShowAllAvatars(true)
  }

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        boxSizing: 'border-box',
        width: '240px',
        height: 'calc(100vh - 64px)',
        borderLeftColor: 'lightGrey.dark',
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid',
        overflowY: 'auto',
        mt: hasOrgBanner ? 13 : 8
      }}>
      {/* A locked period is read only, so the pane says so instead of listing work nobody can
          do. Tasks belong to the open period, but showing them under a locked selection would
          read as editable. */}
      {isReportPeriodLocked === true &&
        <Stack alignItems='center' spacing={1} sx={{ px: 3, pt: 5, textAlign: 'center' }}>
          <LockOutlined sx={{ color: 'text.secondary', fontSize: '2rem' }} />
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            This report period is now locked and no changes can be made.
          </Typography>
        </Stack>}
      {isReportPeriodLocked !== true && <Box sx={{ width: '220px', margin: 'auto' }}>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <Select
            id='actions' name='actions'
            value={expanded}
            size='small'
            onChange={handleChange}
            sx={{ color: 'text.primary', }}>
            <MenuItem value={'panel1'}>Outstanding Tasks</MenuItem>
            <MenuItem value={'panel2'}>Upcoming Tasks</MenuItem>
            <MenuItem value={'panel3'}>Completed Tasks</MenuItem>
          </Select>
        </FormControl>
        <Box>
          <Stack
            display='flex'
            flexWrap='wrap'
            alignItems='start'
            flexDirection='row'
            gap={0.5}
            sx={{ mb: 2, pt: 1, ml: 1, width: '100%' }}>
            {teamUsers &&
              <>
                <Box onClick={() => filterByUser('all')} sx={{ cursor: 'pointer' }}>
                  <AvatarDisplay color={'#e7e7e7'} width={'30px'} height={'30px'} padding={0} fontSize={'0.8rem'} hover={true} showFullNameHover={true} name='All' all={true} />
                </Box>
                <Box onClick={() => filterByUser('unassigned')} sx={{ cursor: 'pointer' }}>
                  <AvatarDisplay color={'#e7e7e7'} width={'30px'} height={'30px'} padding={0} fontSize={'0.8rem'} hover={true} showFullNameHover={true} name='Un' />
                </Box>
                {!showAllAvatars && teamUsers.length <= 4 &&
                  <>
                    {teamUsers.map((user, index) => (
                      <Box onClick={() => filterByUser(user.id)} sx={{ cursor: 'pointer' }} key={index}>
                        <AvatarDisplay color={user.id === selectedUserId ? darken(user.avatarColor, 0.2) : user.avatarColor} width={'30px'} height={'30px'} padding={0} userDetails={user} fontSize={'0.8rem'} hover={true} showFullNameHover={true} />
                      </Box>
                    ))}
                  </>}
                {!showAllAvatars && teamUsers.length > 4 &&
                  <>
                    {teamUsers.slice(0, 3).map((user, index) => (
                      <Box onClick={() => filterByUser(user.id)} sx={{ cursor: 'pointer' }} key={index}>
                        <AvatarDisplay color={user.id === selectedUserId ? darken(user.avatarColor, 0.2) : user.avatarColor} width={'30px'} height={'30px'} padding={0} userDetails={user} fontSize={'0.8rem'} hover={true} showFullNameHover={true} />
                      </Box>
                    ))}
                    <Box onClick={showAllAvatarsHandler} sx={{ cursor: 'pointer' }}>
                      <AvatarDisplay color={'#e7e7e7'} width={'30px'} height={'30px'} padding={0} fontSize={'1rem'} hover={true} name={`+${teamUsers.length - 3}`} />
                    </Box>
                  </>}
                {showAllAvatars && teamUsers.map((user, index) => (
                  <Box onClick={() => filterByUser(user.id)} sx={{ cursor: 'pointer' }} key={index}>
                    <AvatarDisplay color={user.id === selectedUserId ? darken(user.avatarColor, 0.2) : user.avatarColor} width={'30px'} height={'30px'} padding={0} userDetails={user} fontSize={'0.8rem'} hover={true} showFullNameHover={true} />
                  </Box>
                ))}
              </>
            }
          </Stack>
          {actions && actions.length === 0 &&
            <Stack alignItems='center' spacing={1} sx={{ mb: 2, px: 2, pt: 3, textAlign: 'center' }}>
              {/* primary, not success: success.main is MUI's stock green, which is off-brand
                  here and wrong under partner themes. */}
              <CheckCircleOutlined sx={{ color: 'primary.main', fontSize: '2rem' }} />
              <Typography variant='body2' sx={{ color: 'primary.main' }}>
                {expanded === 'panel1' && (selectedUserId === userDetails?.id ? "You're all caught up. No outstanding tasks." : 'All caught up. No outstanding tasks here.')}
                {expanded === 'panel2' && 'No upcoming tasks.'}
                {expanded === 'panel3' && 'No completed tasks yet.'}
              </Typography>
              {selectedUserId !== 'all' &&
                <Typography
                  variant='caption'
                  onClick={() => filterByUser('all')}
                  sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {"See everyone's tasks"}
                </Typography>}
            </Stack>}
          {actions && actions.length > 0 &&
            <Stack sx={{ mb: 2, pl: 1, pr: 1 }}>
              <TableDisplay
                isMobileMode={true}
                headCells={headCells}
                rows={rows}
                selected={selected}
                setSelected={setSelected}
                handleSelected={handleSelected}
                inputPage={inputPage}
                setInputPage={setInputPage}
                totalRecords={totalRecords}
                perPage={perPage}
                setPerPage={setPerPage} />
            </Stack>}
        </Box >
      </Box>}
    </Box>
  )
}